/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { basename, extname } from 'path';
import * as vscode from 'vscode';
import { $Chc } from './commands/commandManager';
import { DocumentSelector } from './configuration/documentSelector';
import * as fileSchemes from './configuration/fileSchemes';
import { LanguageDescription } from './configuration/languageDescription';
import { DiagnosticKind } from './languageFeatures/diagnostics';
import $Kic from './languageFeatures/fileConfigurationManager';
import { TelemetryReporter } from './logging/telemetry';
import { $Nic } from './tsServer/cachedResponse';
import { ClientCapability } from './typescriptService';
import $Hic from './typescriptServiceClient';
import $Oic from './ui/typingsStatus';
import { $whc } from './utils/dispose';
import { $Vhc } from './utils/platform';


const validateSetting = 'validate.enable';
const suggestionSetting = 'suggestionActions.enabled';

export default class $9jc extends $whc {

	constructor(
		private readonly a: $Hic,
		private readonly b: LanguageDescription,
		private readonly e: $Chc,
		private readonly f: TelemetryReporter,
		private readonly i: $Oic,
		private readonly j: $Kic,
		private readonly k: (item: vscode.CompletionItem) => void,
	) {
		super();
		vscode.workspace.onDidChangeConfiguration(this.n, this, this.d);
		this.n();

		a.onReady(() => this.m());
	}

	private get l(): DocumentSelector {
		const semantic: vscode.DocumentFilter[] = [];
		const syntax: vscode.DocumentFilter[] = [];
		for (const language of this.b.languageIds) {
			syntax.push({ language });
			for (const scheme of fileSchemes.$8hc()) {
				semantic.push({ language, scheme });
			}
		}

		return { semantic, syntax };
	}

	private async m(): Promise<void> {
		const selector = this.l;

		const cachedResponse = new $Nic();

		await Promise.all([
			import('./languageFeatures/callHierarchy').then(provider => this.g(provider.$Wic(selector, this.a))),
			import('./languageFeatures/codeLens/implementationsCodeLens').then(provider => this.g(provider.$3ic(selector, this.b, this.a, cachedResponse))),
			import('./languageFeatures/codeLens/referencesCodeLens').then(provider => this.g(provider.$5ic(selector, this.b, this.a, cachedResponse))),
			import('./languageFeatures/completions').then(provider => this.g(provider.$cjc(selector, this.b, this.a, this.i, this.j, this.e, this.f, this.k))),
			import('./languageFeatures/definitions').then(provider => this.g(provider.$fjc(selector, this.a))),
			import('./languageFeatures/directiveCommentCompletions').then(provider => this.g(provider.$gjc(selector, this.a))),
			import('./languageFeatures/documentHighlight').then(provider => this.g(provider.$hjc(selector, this.a))),
			import('./languageFeatures/documentSymbol').then(provider => this.g(provider.$ijc(selector, this.a, cachedResponse))),
			import('./languageFeatures/fileReferences').then(provider => this.g(provider.$jjc(this.a, this.e))),
			import('./languageFeatures/fixAll').then(provider => this.g(provider.$Kjc(selector, this.a, this.j, this.a.diagnosticsManager))),
			import('./languageFeatures/folding').then(provider => this.g(provider.$Ljc(selector, this.a))),
			import('./languageFeatures/formatting').then(provider => this.g(provider.$Mjc(selector, this.b, this.a, this.j))),
			import('./languageFeatures/hover').then(provider => this.g(provider.$Njc(selector, this.a, this.j))),
			import('./languageFeatures/implementations').then(provider => this.g(provider.$Ojc(selector, this.a))),
			import('./languageFeatures/inlayHints').then(provider => this.g(provider.$Pjc(selector, this.b, this.a, this.j))),
			import('./languageFeatures/jsDocCompletions').then(provider => this.g(provider.$Rjc(selector, this.b, this.a, this.j))),
			import('./languageFeatures/linkedEditing').then(provider => this.g(provider.$Sjc(selector, this.a))),
			import('./languageFeatures/organizeImports').then(provider => this.g(provider.$Tjc(selector, this.a, this.e, this.j, this.f))),
			import('./languageFeatures/quickFix').then(provider => this.g(provider.$Ujc(selector, this.a, this.j, this.e, this.a.diagnosticsManager, this.f))),
			import('./languageFeatures/refactor').then(provider => this.g(provider.$Yjc(selector, this.a, this.j, this.e, this.f))),
			import('./languageFeatures/references').then(provider => this.g(provider.$Zjc(selector, this.a))),
			import('./languageFeatures/rename').then(provider => this.g(provider.$1jc(selector, this.b, this.a, this.j))),
			import('./languageFeatures/semanticTokens').then(provider => this.g(provider.$2jc(selector, this.a))),
			import('./languageFeatures/signatureHelp').then(provider => this.g(provider.$3jc(selector, this.a))),
			import('./languageFeatures/smartSelect').then(provider => this.g(provider.$4jc(selector, this.a))),
			import('./languageFeatures/sourceDefinition').then(provider => this.g(provider.$5jc(this.a, this.e))),
			import('./languageFeatures/tagClosing').then(provider => this.g(provider.$6jc(selector, this.b, this.a))),
			import('./languageFeatures/typeDefinitions').then(provider => this.g(provider.$8jc(selector, this.a))),
		]);
	}

	private n(): void {
		const config = vscode.workspace.getConfiguration(this.p, null);
		this.q(config.get(validateSetting, true));
		this.r(config.get(suggestionSetting, true));
	}

	public handlesUri(resource: vscode.Uri): boolean {
		const ext = extname(resource.path).slice(1).toLowerCase();
		return this.b.standardFileExtensions.includes(ext) || this.o(resource);
	}

	public handlesDocument(doc: vscode.TextDocument): boolean {
		return this.b.languageIds.includes(doc.languageId) || this.o(doc.uri);
	}

	private o(resource: vscode.Uri) {
		const base = basename(resource.fsPath);
		return !!base && (!!this.b.configFilePattern && this.b.configFilePattern.test(base));
	}

	private get p(): string {
		return this.b.id;
	}

	public get diagnosticSource(): string {
		return this.b.diagnosticSource;
	}

	private q(value: boolean) {
		this.a.diagnosticsManager.setValidate(this.s, value);
	}

	private r(value: boolean) {
		this.a.diagnosticsManager.setEnableSuggestions(this.s, value);
	}

	public reInitialize(): void {
		this.a.diagnosticsManager.reInitialize();
	}

	public triggerAllDiagnostics(): void {
		this.a.bufferSyncSupport.requestAllDiagnostics();
	}

	public diagnosticsReceived(diagnosticsKind: DiagnosticKind, file: vscode.Uri, diagnostics: (vscode.Diagnostic & { reportUnnecessary: any; reportDeprecated: any })[]): void {
		if (diagnosticsKind !== DiagnosticKind.Syntax && !this.a.hasCapabilityForResource(file, ClientCapability.Semantic)) {
			return;
		}

		if (diagnosticsKind === DiagnosticKind.Semantic && $Vhc() && this.a.configuration.webProjectWideIntellisenseSuppressSemanticErrors) {
			return;
		}

		const config = vscode.workspace.getConfiguration(this.p, file);
		const reportUnnecessary = config.get<boolean>('showUnused', true);
		const reportDeprecated = config.get<boolean>('showDeprecated', true);
		this.a.diagnosticsManager.updateDiagnostics(file, this.s, diagnosticsKind, diagnostics.filter(diag => {
			// Don't bother reporting diagnostics we know will not be rendered
			if (!reportUnnecessary) {
				if (diag.reportUnnecessary && diag.severity === vscode.DiagnosticSeverity.Hint) {
					return false;
				}
			}
			if (!reportDeprecated) {
				if (diag.reportDeprecated && diag.severity === vscode.DiagnosticSeverity.Hint) {
					return false;
				}
			}
			return true;
		}));
	}

	public configFileDiagnosticsReceived(file: vscode.Uri, diagnostics: vscode.Diagnostic[]): void {
		this.a.diagnosticsManager.configFileDiagnosticsReceived(file, diagnostics);
	}

	private get s() {
		return this.b.diagnosticLanguage;
	}
}
